<template>
  <div class="chart-container">
    <TimeRangeFilter 
      chartId="user-registration" 
      @range-changed="handleRangeChange" 
    />
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from "vue";
import Chart from "chart.js/auto";
import TimeRangeFilter from "@/components/charts/TimeRangeFilter.vue";
import apiClient from "@/services/api";
import { useTheme } from "@/composables/useTheme";

// Resolves a themed HSL custom property (e.g. "--foreground") into a real
// color string Chart.js/canvas can render, honoring light/dark mode.
function themeColor(varName, alpha) {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  if (!value) return alpha != null ? `hsla(0, 0%, 50%, ${alpha})` : "#64748b";
  return alpha != null ? `hsl(${value} / ${alpha})` : `hsl(${value})`;
}

export default {
  name: "UserRegistrationChart",
  components: {
    TimeRangeFilter
  },
  props: {
    chartData: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const chartCanvas = ref(null);
    const timeRange = ref('month');
    let chart = null;
    const isLoading = ref(false);
    const localChartData = ref({...props.chartData});
    const { theme } = useTheme();

    const createChart = () => {
      if (!chartCanvas.value) return;

      if (chart) {
        chart.destroy();
        chart = null;
      }

      const ctx = chartCanvas.value.getContext("2d");
      const foreground = themeColor("--foreground");
      const mutedForeground = themeColor("--muted-foreground");
      const gridColor = themeColor("--border", 0.5);

      chart = new Chart(ctx, {
        type: "line",
        data: {
          labels: localChartData.value.labels || [],
          datasets: [
            {
              label: "Job Seekers",
              data: localChartData.value.jobseekers || [],
              borderColor: "#87CEEB", // Light blue
              backgroundColor: "rgba(135, 206, 235, 0.1)",
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointBackgroundColor: "#87CEEB",
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
              pointRadius: 6,
              pointHoverRadius: 8,
            },
            {
              label: "Employers",
              data: localChartData.value.employers || [],
              borderColor: "#9370DB", // Purple
              backgroundColor: "rgba(147, 112, 219, 0.1)",
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointBackgroundColor: "#9370DB",
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
              pointRadius: 6,
              pointHoverRadius: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: getChartTitle(),
              font: {
                size: 18,
                weight: "bold",
              },
              color: foreground,
            },
            legend: {
              display: true,
              position: "top",
              labels: {
                usePointStyle: true,
                padding: 20,
                color: mutedForeground,
                font: {
                  size: 14,
                },
              },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              titleColor: "#ffffff",
              bodyColor: "#ffffff",
              borderColor: gridColor,
              borderWidth: 1,
            },
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: getXAxisTitle(),
                font: {
                  size: 14,
                  weight: "600",
                },
                color: mutedForeground,
              },
              grid: {
                color: gridColor,
                drawBorder: false,
              },
              ticks: {
                color: mutedForeground,
                font: {
                  size: 12,
                },
              },
            },
            y: {
              display: true,
              title: {
                display: true,
                text: "Number of Users",
                font: {
                  size: 14,
                  weight: "600",
                },
                color: mutedForeground,
              },
              grid: {
                color: gridColor,
                drawBorder: false,
              },
              ticks: {
                color: mutedForeground,
                font: {
                  size: 12,
                },
                stepSize: 1,
                callback: function (value) {
                  return Math.round(value);
                },
              },
              beginAtZero: true,
            },
          },
          interaction: {
            mode: "nearest",
            axis: "x",
            intersect: false,
          },
          elements: {
            point: {
              hoverBackgroundColor: "#ffffff",
            },
          },
        },
      });
    };

    const getChartTitle = () => {
      switch(timeRange.value) {
        case 'day': return 'User Registration (Daily)';
        case 'week': return 'User Registration (Weekly)';
        default: return 'User Registration (Monthly)';
      }
    };

    const getXAxisTitle = () => {
      switch(timeRange.value) {
        case 'day': return 'Day';
        case 'week': return 'Week';
        default: return 'Month';
      }
    };

    const updateChart = () => {
      if (chart) {
        chart.data.labels = localChartData.value.labels || [];
        chart.data.datasets[0].data = localChartData.value.jobseekers || [];
        chart.data.datasets[1].data = localChartData.value.employers || [];
        
        // Update titles based on time range
        chart.options.plugins.title.text = getChartTitle();
        chart.options.scales.x.title.text = getXAxisTitle();
        
        chart.update();
      }
    };

    const destroyChart = () => {
      if (chart) {
        chart.destroy();
        chart = null;
      }
    };

    const handleRangeChange = async (event) => {
      timeRange.value = event.timeRange;
      await fetchData();
    };

    const fetchData = async () => {
      try {
        isLoading.value = true;
        // Fix endpoint URL to match backend URLs
        const endpoint = timeRange.value === 'month' ? 'monthly' : timeRange.value;
        const response = await apiClient.get(`/admin-dashboard/user-registration-${endpoint}/`);
        localChartData.value = response.data;
        
        if (chart) {
          updateChart();
        } else {
          createChart();
        }
      } catch (error) {
        console.error(`Error fetching ${timeRange.value} user registration data:`, error);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      // Use the provided data initially
      localChartData.value = props.chartData;
      createChart();
    });

    onUnmounted(() => {
      destroyChart();
    });

    // Rebuild the chart when the theme toggles so canvas text/grid colors
    // (which can't respond to CSS custom properties on their own) stay legible.
    watch(theme, () => {
      createChart();
    });

    watch(
      () => props.chartData,
      (newData) => {
        if (timeRange.value === 'month') {
          localChartData.value = newData;
          updateChart();
        }
      },
      { deep: true }
    );

    return {
      chartCanvas,
      handleRangeChange,
      isLoading,
    };
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 400px;
  position: relative;
  background: hsl(var(--card));
  color: hsl(var(--foreground));
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid hsl(var(--border));
}

@media (max-width: 768px) {
  .chart-container {
    height: 350px;
    padding: 1rem;
  }
}
</style>
